import type { SerializedError } from "./types";

export function serializeError(error: unknown): SerializedError {
  if (!error) {
    return {
      name: "UnknownError",
      message: "No error information available",
    };
  }

  // Handle Error objects
  if (error instanceof Error) {
    return handleDefaultError(error);
  }

  // Handle string errors
  if (typeof error === "string") {
    return {
      name: "StringError",
      message: error,
    };
  }

  // Handle object errors
  if (typeof error === "object") {
    const obj = error as Record<string, unknown>;
    return {
      name: (obj.name as string) || "ObjectError",
      message: (obj.message as string) || JSON.stringify(error),
      code: obj.code as string | number,
      statusCode: obj.statusCode as number,
      metadata: sanitizeObject(obj),
    };
  }

  // Handle primitive errors
  return {
    name: "PrimitiveError",
    message: JSON.stringify(error),
  };
}

function handleDefaultError(error: Error): SerializedError {
  const serialized: SerializedError = {
    name: error.name,
    message: error.message,
    stack:
      error.stack?.split("\n").slice(0, 4).join("\n") ??
      "No stack trace available",
  };

  // Handle additional properties that might exist on error objects
  if ("code" in error) {
    serialized.code = (error as { code: string | number }).code;
  }
  if ("statusCode" in error) {
    serialized.statusCode = (error as { statusCode: number }).statusCode;
  }
  if ("cause" in error && error.cause) {
    serialized.cause = serializeError(error.cause);
  }

  handleUnknownErrorKeys(serialized, error);

  return serialized;
}

function handleUnknownErrorKeys(
  serialized: SerializedError,
  error: Error,
): void {
  // Capture any additional enumerable properties
  const additionalProps: Record<string, unknown> = {};
  for (const key in error) {
    if (
      !["name", "message", "stack", "cause", "code", "statusCode"].includes(key)
    ) {
      try {
        const value = (error as unknown as Record<string, unknown>)[key];
        if (value !== undefined && typeof value !== "function") {
          additionalProps[key] = sanitizeValue(value);
        }
      } catch {
        // Ignore properties that can't be accessed
      }
    }
  }

  if (Object.keys(additionalProps).length > 0) {
    serialized.metadata = additionalProps;
  }
}

function sanitizeValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.map(sanitizeValue).slice(0, 10); // Limit array size
  }

  if (typeof value === "object") {
    return sanitizeObject(value as Record<string, unknown>);
  }

  return JSON.stringify(value);
}

function sanitizeObject(obj: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};
  let count = 0;
  for (const key in obj) {
    if (count >= 20) break; // Limit object size
    // Skip sensitive keys
    try {
      if (isSensitiveKey(key)) {
        sanitized[key] = "[REDACTED]";
      } else {
        sanitized[key] = sanitizeValue(obj[key]);
      }
    } catch {
      sanitized[key] = "[SERIALIZATION_ERROR]";
    }
    count++;
  }

  return sanitized;
}

function isSensitiveKey(key: string): boolean {
  const sensitiveKeys = [
    "password",
    "token",
    "secret",
    "key",
    "auth",
    "credential",
    "cookie",
    "session",
    "authorization",
    "x-api-key",
  ];

  return sensitiveKeys.some((sensitive) =>
    key.toLowerCase().includes(sensitive),
  );
}

export function sanitizeLogData(data: unknown): unknown {
  try {
    return sanitizeValue(data);
  } catch {
    return "[SANITIZATION_ERROR]";
  }
}
